import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Play, Pause, ChevronDown, ChevronRight, CircleAlert as AlertCircle, TrendingDown, TrendingUp, Music, RotateCcw, Volume2, VolumeX, Disc, Square, SkipBack } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface LyricLine {
  time: number;
  endTime: number;
  text: string;
}

interface LineAnalysis {
  line: LyricLine;
  index: number;
  avgDeviation: number;
  maxDeviation: number;
  minDeviation: number;
  stdDeviation: number;
  flatPercent: number;
  sharpPercent: number;
  dataPointCount: number;
  severity: 'good' | 'fair' | 'needs-work' | 'problem';
  primaryIssue: 'flat' | 'sharp' | 'unstable' | 'none';
  timePoints: number[];
  deviations: number[];
  referencePitch: number[];
  recordedPitch: number[];
}

interface ReportPracticeGuideProps {
  report: any;
  lyricsUrl?: string;
  vocalRecordingUrl?: string;
  leadVocalUrl?: string;
}

type SeverityFilter = 'all' | 'good' | 'fair' | 'needs-work' | 'problem';
type PlaybackMode = 'section' | 'original';

const parseLRC = (content: string): LyricLine[] => {
  const lines: LyricLine[] = [];
  const regex = /\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    const centiseconds = match[3].length === 2 ? parseInt(match[3], 10) * 10 : parseInt(match[3], 10);
    const time = minutes * 60 + seconds + centiseconds / 1000;
    let text = match[4].trim();

    text = text.replace(/<\d{2}:\d{2}\.\d{2,3}>/g, '');
    text = text.replace(/\s+/g, ' ').trim();

    if (text) {
      lines.push({ time, endTime: 0, text });
    }
  }

  for (let i = 0; i < lines.length; i++) {
    lines[i].endTime = i < lines.length - 1 ? lines[i + 1].time : lines[i].time + 5;
  }

  return lines;
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'good': return 'border-green-500 bg-green-500/10';
    case 'fair': return 'border-yellow-500 bg-yellow-500/10';
    case 'needs-work': return 'border-orange-500 bg-orange-500/10';
    case 'problem': return 'border-red-500 bg-red-500/10';
    default: return 'border-gray-500 bg-gray-500/10';
  }
};

const getSeverityBadge = (severity: string) => {
  switch (severity) {
    case 'good': return { text: 'Good', class: 'bg-green-500/20 text-green-400' };
    case 'fair': return { text: 'Fair', class: 'bg-yellow-500/20 text-yellow-400' };
    case 'needs-work': return { text: 'Needs Work', class: 'bg-orange-500/20 text-orange-400' };
    case 'problem': return { text: 'Problem', class: 'bg-red-500/20 text-red-400' };
    default: return { text: 'N/A', class: 'bg-gray-500/20 text-gray-400' };
  }
};

const getDeviationColor = (deviation: number): string => {
  const absDeviation = Math.abs(deviation);
  if (absDeviation < 0.15) return '#22c55e';
  if (absDeviation < 0.3) return '#eab308';
  if (absDeviation < 0.5) return '#f97316';
  return '#ef4444';
};

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const ReportPracticeGuide: React.FC<ReportPracticeGuideProps> = ({
  report,
  lyricsUrl,
  vocalRecordingUrl,
  leadVocalUrl
}) => {
  const [lyrics, setLyrics] = useState<LyricLine[]>([]);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [lyricsError, setLyricsError] = useState<string>('');
  const [expandedLine, setExpandedLine] = useState<number | null>(null);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackMode, setPlaybackMode] = useState<PlaybackMode>('section');
  const [currentPlayingLine, setCurrentPlayingLine] = useState<number | null>(null);
  const [loopMode, setLoopMode] = useState(false);
  const [showFocusAreas, setShowFocusAreas] = useState(false);
  const [fullSongMode, setFullSongMode] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState<number | null>(null);
  const [currentDeviation, setCurrentDeviation] = useState<number>(0);

  const vocalAudioRef = useRef<HTMLAudioElement | null>(null);
  const leadVocalAudioRef = useRef<HTMLAudioElement | null>(null);
  const lineRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const liveChartRef = useRef<any>(null);
  const animationRef = useRef<number | null>(null);

  const scrollLineIntoView = useCallback((index: number, smooth = true) => {
    const container = listContainerRef.current;
    const element = lineRefs.current[index];
    if (!container || !element) return;
    const target = element.offsetTop - container.clientHeight / 2 + element.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const { results = {}, pitch_curves = {} } = report;
  const { anomaly_curve = [], time_points = [] } = results;
  const { reference = [], recording = [] } = pitch_curves;
  const totalDuration = time_points?.length ? time_points[time_points.length - 1] : 0;

  useEffect(() => {
    if (!lyricsUrl) return;

    const fetchLyrics = async () => {
      setLyricsLoading(true);
      setLyricsError('');
      try {
        const response = await fetch(lyricsUrl);
        if (!response.ok) throw new Error('Failed to fetch lyrics');
        const text = await response.text();
        const parsed = parseLRC(text);
        setLyrics(parsed);
      } catch (err: any) {
        console.error('Error fetching lyrics:', err);
        setLyricsError(err.message || 'Failed to load lyrics');
      } finally {
        setLyricsLoading(false);
      }
    };

    fetchLyrics();
  }, [lyricsUrl]);

  const lineAnalyses = useMemo((): LineAnalysis[] => {
    if (!lyrics.length || !time_points.length || !anomaly_curve.length) {
      return [];
    }

    return lyrics.map((line, index) => {
      const lineTimePoints: number[] = [];
      const lineDeviations: number[] = [];
      const lineRefPitch: number[] = [];
      const lineRecPitch: number[] = [];

      for (let i = 0; i < time_points.length; i++) {
        const t = time_points[i];
        if (t >= line.time && t < line.endTime) {
          lineTimePoints.push(t);
          lineDeviations.push(anomaly_curve[i] || 0);
          if (reference[i] !== undefined) lineRefPitch.push(reference[i]);
          if (recording[i] !== undefined) lineRecPitch.push(recording[i]);
        }
      }

      if (lineDeviations.length === 0) {
        return {
          line,
          index,
          avgDeviation: 0,
          maxDeviation: 0,
          minDeviation: 0,
          stdDeviation: 0,
          flatPercent: 0,
          sharpPercent: 0,
          dataPointCount: 0,
          severity: 'good' as const,
          primaryIssue: 'none' as const,
          timePoints: lineTimePoints,
          deviations: lineDeviations,
          referencePitch: lineRefPitch,
          recordedPitch: lineRecPitch
        };
      }

      const absDeviations = lineDeviations.map(d => Math.abs(d));
      const avgDeviation = absDeviations.reduce((a, b) => a + b, 0) / absDeviations.length;
      const maxDeviation = Math.max(...absDeviations);
      const minDeviation = Math.min(...absDeviations);

      const mean = lineDeviations.reduce((a, b) => a + b, 0) / lineDeviations.length;
      const variance = lineDeviations.reduce((sum, d) => sum + Math.pow(d - mean, 2), 0) / lineDeviations.length;
      const stdDeviation = Math.sqrt(variance);

      const flatCount = lineDeviations.filter(d => d < -0.05).length;
      const sharpCount = lineDeviations.filter(d => d > 0.05).length;
      const flatPercent = (flatCount / lineDeviations.length) * 100;
      const sharpPercent = (sharpCount / lineDeviations.length) * 100;

      let severity: 'good' | 'fair' | 'needs-work' | 'problem';
      if (avgDeviation < 0.15) severity = 'good';
      else if (avgDeviation < 0.3) severity = 'fair';
      else if (avgDeviation < 0.5) severity = 'needs-work';
      else severity = 'problem';

      let primaryIssue: 'flat' | 'sharp' | 'unstable' | 'none';
      if (stdDeviation > 0.4) primaryIssue = 'unstable';
      else if (flatPercent > 60) primaryIssue = 'flat';
      else if (sharpPercent > 60) primaryIssue = 'sharp';
      else if (avgDeviation < 0.15) primaryIssue = 'none';
      else primaryIssue = flatPercent > sharpPercent ? 'flat' : 'sharp';

      return {
        line,
        index,
        avgDeviation,
        maxDeviation,
        minDeviation,
        stdDeviation,
        flatPercent,
        sharpPercent,
        dataPointCount: lineDeviations.length,
        severity,
        primaryIssue,
        timePoints: lineTimePoints,
        deviations: lineDeviations,
        referencePitch: lineRefPitch,
        recordedPitch: lineRecPitch
      };
    });
  }, [lyrics, time_points, anomaly_curve, reference, recording]);

  const filteredAnalyses = useMemo(() => {
    if (severityFilter === 'all') return lineAnalyses;
    return lineAnalyses.filter(a => a.severity === severityFilter);
  }, [lineAnalyses, severityFilter]);

  const focusAreas = useMemo(() => {
    const problemLines = lineAnalyses.filter(a => a.severity === 'needs-work' || a.severity === 'problem');
    const groups: { lines: LineAnalysis[]; commonIssue: string }[] = [];
    let currentGroup: LineAnalysis[] = [];

    problemLines.forEach((analysis) => {
      if (currentGroup.length === 0 || analysis.index === currentGroup[currentGroup.length - 1].index + 1) {
        currentGroup.push(analysis);
      } else {
        if (currentGroup.length > 0) {
          const flatCount = currentGroup.filter(a => a.primaryIssue === 'flat').length;
          const sharpCount = currentGroup.filter(a => a.primaryIssue === 'sharp').length;
          const unstableCount = currentGroup.filter(a => a.primaryIssue === 'unstable').length;
          let commonIssue = 'Mixed issues';
          if (flatCount > sharpCount && flatCount > unstableCount) commonIssue = 'Consistently flat';
          else if (sharpCount > flatCount && sharpCount > unstableCount) commonIssue = 'Consistently sharp';
          else if (unstableCount > flatCount && unstableCount > sharpCount) commonIssue = 'Pitch instability';
          groups.push({ lines: currentGroup, commonIssue });
        }
        currentGroup = [analysis];
      }
    });

    if (currentGroup.length > 0) {
      const flatCount = currentGroup.filter(a => a.primaryIssue === 'flat').length;
      const sharpCount = currentGroup.filter(a => a.primaryIssue === 'sharp').length;
      const unstableCount = currentGroup.filter(a => a.primaryIssue === 'unstable').length;
      let commonIssue = 'Mixed issues';
      if (flatCount > sharpCount && flatCount > unstableCount) commonIssue = 'Consistently flat';
      else if (sharpCount > flatCount && sharpCount > unstableCount) commonIssue = 'Consistently sharp';
      else if (unstableCount > flatCount && unstableCount > sharpCount) commonIssue = 'Pitch instability';
      groups.push({ lines: currentGroup, commonIssue });
    }

    return groups;
  }, [lineAnalyses]);

  const summaryStats = useMemo(() => {
    const total = lineAnalyses.length;
    const good = lineAnalyses.filter(a => a.severity === 'good').length;
    const fair = lineAnalyses.filter(a => a.severity === 'fair').length;
    const needsWork = lineAnalyses.filter(a => a.severity === 'needs-work').length;
    const problem = lineAnalyses.filter(a => a.severity === 'problem').length;
    return { total, good, fair, needsWork, problem };
  }, [lineAnalyses]);

  const getDeviationAtTime = useCallback((time: number): number => {
    if (!time_points.length || !anomaly_curve.length) return 0;

    let closestIdx = 0;
    let minDiff = Math.abs(time_points[0] - time);

    for (let i = 1; i < time_points.length; i++) {
      const diff = Math.abs(time_points[i] - time);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = i;
      }
    }

    return anomaly_curve[closestIdx] || 0;
  }, [time_points, anomaly_curve]);

  const getLineAtTime = useCallback((time: number): number | null => {
    for (let i = 0; i < lineAnalyses.length; i++) {
      if (time >= lineAnalyses[i].line.time && time < lineAnalyses[i].line.endTime) {
        return i;
      }
    }
    return null;
  }, [lineAnalyses]);

  const playSection = (analysis: LineAnalysis, mode: PlaybackMode) => {
    const audio = mode === 'original' ? leadVocalAudioRef.current : vocalAudioRef.current;
    if (!audio) return;

    if (vocalAudioRef.current) vocalAudioRef.current.pause();
    if (leadVocalAudioRef.current) leadVocalAudioRef.current.pause();

    audio.currentTime = analysis.line.time;
    audio.play();
    setIsPlaying(true);
    setPlaybackMode(mode);
    setCurrentPlayingLine(analysis.index);
    setFullSongMode(false);
  };

  const stopPlayback = useCallback(() => {
    if (vocalAudioRef.current) vocalAudioRef.current.pause();
    if (leadVocalAudioRef.current) leadVocalAudioRef.current.pause();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    setIsPlaying(false);
    setCurrentPlayingLine(null);
    setFullSongMode(false);
    setCurrentTime(0);
    setCurrentLineIndex(null);
  }, []);

  const startFullSongPlayback = (mode: PlaybackMode) => {
    const audio = mode === 'original' ? leadVocalAudioRef.current : vocalAudioRef.current;
    if (!audio) return;

    if (vocalAudioRef.current) vocalAudioRef.current.pause();
    if (leadVocalAudioRef.current) leadVocalAudioRef.current.pause();

    audio.currentTime = 0;
    audio.play();
    setIsPlaying(true);
    setPlaybackMode(mode);
    setFullSongMode(true);
    setCurrentPlayingLine(null);
  };

  useEffect(() => {
    const vocalAudio = vocalAudioRef.current;
    const leadVocalAudio = leadVocalAudioRef.current;

    const handleTimeUpdate = (audio: HTMLAudioElement) => {
      const time = audio.currentTime;
      setCurrentTime(time);

      if (fullSongMode) {
        const lineIdx = getLineAtTime(time);
        setCurrentLineIndex(lineIdx);
        const deviation = getDeviationAtTime(time);
        setCurrentDeviation(deviation);

        if (lineIdx !== null) {
          scrollLineIntoView(lineIdx, true);
        }
      } else if (currentPlayingLine !== null) {
        const analysis = lineAnalyses[currentPlayingLine];
        if (analysis) {
          if (loopMode && time >= analysis.line.endTime) {
            audio.currentTime = analysis.line.time;
          } else if (!loopMode && time >= analysis.line.endTime) {
            stopPlayback();
          }
        }
      }
    };

    const handleEnded = () => {
      stopPlayback();
    };

    const vocalHandler = () => vocalAudio && handleTimeUpdate(vocalAudio);
    const leadVocalHandler = () => leadVocalAudio && handleTimeUpdate(leadVocalAudio);

    if (vocalAudio) {
      vocalAudio.addEventListener('timeupdate', vocalHandler);
      vocalAudio.addEventListener('ended', handleEnded);
    }
    if (leadVocalAudio) {
      leadVocalAudio.addEventListener('timeupdate', leadVocalHandler);
      leadVocalAudio.addEventListener('ended', handleEnded);
    }

    return () => {
      if (vocalAudio) {
        vocalAudio.removeEventListener('timeupdate', vocalHandler);
        vocalAudio.removeEventListener('ended', handleEnded);
      }
      if (leadVocalAudio) {
        leadVocalAudio.removeEventListener('timeupdate', leadVocalHandler);
        leadVocalAudio.removeEventListener('ended', handleEnded);
      }
    };
  }, [currentPlayingLine, loopMode, lineAnalyses, fullSongMode, getLineAtTime, getDeviationAtTime, stopPlayback, scrollLineIntoView]);

  const scrollToLine = (index: number) => {
    setExpandedLine(index);
    requestAnimationFrame(() => scrollLineIntoView(index, true));
  };

  const chartColors = {
    reference: '#3b82f6',
    recorded: '#f97316',
    grid: 'rgba(255, 255, 255, 0.1)',
    text: 'rgba(255, 255, 255, 0.7)'
  };

  const getLineChartData = (analysis: LineAnalysis) => {
    const labels = analysis.timePoints.map(t => formatTime(t));
    return {
      labels,
      datasets: [
        {
          label: 'Reference',
          data: analysis.referencePitch,
          borderColor: chartColors.reference,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          fill: false
        },
        {
          label: 'Your Recording',
          data: analysis.recordedPitch,
          borderColor: chartColors.recorded,
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          fill: false
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: chartColors.text, font: { size: 11 } }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false
      }
    },
    scales: {
      x: {
        grid: { color: chartColors.grid },
        ticks: { color: chartColors.text, font: { size: 10 } }
      },
      y: {
        grid: { color: chartColors.grid },
        ticks: { color: chartColors.text, font: { size: 10 } },
        title: { display: true, text: 'Pitch', color: chartColors.text }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  if (lyricsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent"></div>
        <span className="ml-3 text-white/70">Loading lyrics...</span>
      </div>
    );
  }

  if (!lyricsUrl) {
    return (
      <div className="p-6">
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-yellow-400 font-medium">Lyrics Not Available</p>
            <p className="text-yellow-300/80 text-sm mt-1">
              No lyrics URL provided for this song. The Practice Guide requires lyrics to align with performance data.
            </p>
          </div>
        </div>
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Time-Based Analysis</h3>
          <p className="text-white/60 text-sm">
            Total duration: {formatTime(totalDuration)} | Data points: {time_points.length}
          </p>
        </div>
      </div>
    );
  }

  if (lyricsError) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-red-400 font-medium">Failed to Load Lyrics</p>
            <p className="text-red-300/80 text-sm mt-1">{lyricsError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {vocalRecordingUrl && (
        <audio ref={vocalAudioRef} src={vocalRecordingUrl} preload="metadata" />
      )}
      {leadVocalUrl && (
        <audio ref={leadVocalAudioRef} src={leadVocalUrl} preload="metadata" />
      )}

      <div className="flex-shrink-0 p-4 border-b border-kod-purple-600 bg-kod-purple-900/30">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <button
            onClick={() => setSeverityFilter(severityFilter === 'all' ? 'all' : 'all')}
            className={`rounded-lg p-3 text-center transition-all ${
              severityFilter === 'all'
                ? 'bg-kod-purple-600 ring-2 ring-kod-accent'
                : 'bg-kod-purple-700/50 hover:bg-kod-purple-700'
            }`}
          >
            <div className="text-2xl font-bold text-white">{summaryStats.total}</div>
            <div className="text-xs text-white/60">Total Lines</div>
          </button>
          <button
            onClick={() => setSeverityFilter(severityFilter === 'good' ? 'all' : 'good')}
            className={`rounded-lg p-3 text-center transition-all ${
              severityFilter === 'good'
                ? 'bg-green-600/40 ring-2 ring-green-400'
                : 'bg-green-500/20 hover:bg-green-500/30'
            }`}
          >
            <div className="text-2xl font-bold text-green-400">{summaryStats.good}</div>
            <div className="text-xs text-green-400/80">Good</div>
          </button>
          <button
            onClick={() => setSeverityFilter(severityFilter === 'fair' ? 'all' : 'fair')}
            className={`rounded-lg p-3 text-center transition-all ${
              severityFilter === 'fair'
                ? 'bg-yellow-600/40 ring-2 ring-yellow-400'
                : 'bg-yellow-500/20 hover:bg-yellow-500/30'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-400">{summaryStats.fair}</div>
            <div className="text-xs text-yellow-400/80">Fair</div>
          </button>
          <button
            onClick={() => setSeverityFilter(severityFilter === 'needs-work' ? 'all' : 'needs-work')}
            className={`rounded-lg p-3 text-center transition-all ${
              severityFilter === 'needs-work'
                ? 'bg-orange-600/40 ring-2 ring-orange-400'
                : 'bg-orange-500/20 hover:bg-orange-500/30'
            }`}
          >
            <div className="text-2xl font-bold text-orange-400">{summaryStats.needsWork}</div>
            <div className="text-xs text-orange-400/80">Needs Work</div>
          </button>
          <button
            onClick={() => setSeverityFilter(severityFilter === 'problem' ? 'all' : 'problem')}
            className={`rounded-lg p-3 text-center transition-all ${
              severityFilter === 'problem'
                ? 'bg-red-600/40 ring-2 ring-red-400'
                : 'bg-red-500/20 hover:bg-red-500/30'
            }`}
          >
            <div className="text-2xl font-bold text-red-400">{summaryStats.problem}</div>
            <div className="text-xs text-red-400/80">Problem</div>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(vocalRecordingUrl || leadVocalUrl) && (
            <>
              <span className="text-sm text-white/60">Full Song:</span>
              {vocalRecordingUrl && (
                <button
                  onClick={() => fullSongMode && isPlaying && playbackMode === 'section' ? stopPlayback() : startFullSongPlayback('section')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    fullSongMode && isPlaying && playbackMode === 'section'
                      ? 'bg-kod-accent text-white'
                      : 'bg-kod-purple-700/50 text-white/70 hover:bg-kod-purple-700 hover:text-white'
                  }`}
                >
                  {fullSongMode && isPlaying && playbackMode === 'section' ? <Pause size={14} /> : <Play size={14} />}
                  Play Recording
                </button>
              )}
              {leadVocalUrl && (
                <button
                  onClick={() => fullSongMode && isPlaying && playbackMode === 'original' ? stopPlayback() : startFullSongPlayback('original')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    fullSongMode && isPlaying && playbackMode === 'original'
                      ? 'bg-blue-500 text-white'
                      : 'bg-kod-purple-700/50 text-white/70 hover:bg-kod-purple-700 hover:text-white'
                  }`}
                >
                  {fullSongMode && isPlaying && playbackMode === 'original' ? <Pause size={14} /> : <Disc size={14} />}
                  Play Original
                </button>
              )}

              {isPlaying && (
                <button
                  onClick={stopPlayback}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  <Square size={14} /> Stop
                </button>
              )}

              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => setLoopMode(!loopMode)}
                  className={`p-2 rounded-lg transition-colors ${
                    loopMode ? 'bg-kod-accent text-white' : 'bg-kod-purple-700/50 text-white/70 hover:bg-kod-purple-700'
                  }`}
                  title={loopMode ? 'Loop mode on' : 'Loop mode off'}
                >
                  <RotateCcw size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {fullSongMode && isPlaying && (
        <div className="flex-shrink-0 border-b border-kod-purple-600 bg-kod-purple-900/50 p-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="text-sm text-white/70">{formatTime(currentTime)} / {formatTime(totalDuration)}</div>
            <div className="flex-1 h-2 bg-kod-purple-700 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-100"
                style={{
                  width: `${(currentTime / totalDuration) * 100}%`,
                  backgroundColor: getDeviationColor(currentDeviation)
                }}
              />
            </div>
            <div
              className="px-3 py-1 rounded text-sm font-medium"
              style={{ backgroundColor: `${getDeviationColor(currentDeviation)}30`, color: getDeviationColor(currentDeviation) }}
            >
              {currentDeviation > 0 ? '+' : ''}{currentDeviation.toFixed(2)} {currentDeviation > 0.05 ? 'Sharp' : currentDeviation < -0.05 ? 'Flat' : 'On Pitch'}
            </div>
          </div>

          {currentLineIndex !== null && lineAnalyses[currentLineIndex] && (
            <div
              className={`text-center p-3 rounded-lg border-l-4 ${getSeverityColor(lineAnalyses[currentLineIndex].severity)}`}
            >
              <div className="text-lg font-medium text-white">
                {lineAnalyses[currentLineIndex].line.text}
              </div>
            </div>
          )}
        </div>
      )}

      {focusAreas.length > 0 && !fullSongMode && (
        <div className="flex-shrink-0 border-b border-kod-purple-600 flex">
          <button
            onClick={() => setShowFocusAreas(false)}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              !showFocusAreas
                ? 'text-white border-b-2 border-kod-accent bg-kod-purple-700/30'
                : 'text-white/60 hover:text-white hover:bg-kod-purple-700/20'
            }`}
          >
            All Lines
          </button>
          <button
            onClick={() => setShowFocusAreas(true)}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              showFocusAreas
                ? 'text-red-400 border-b-2 border-red-400 bg-red-500/10'
                : 'text-white/60 hover:text-white hover:bg-kod-purple-700/20'
            }`}
          >
            <AlertCircle size={14} />
            Focus Areas ({focusAreas.length})
          </button>
        </div>
      )}

      {showFocusAreas && focusAreas.length > 0 && !fullSongMode ? (
        <div className="flex-1 min-h-0 overflow-auto p-4 space-y-3">
          {focusAreas.map((area, idx) => (
            <div key={idx} className="bg-kod-purple-800/50 rounded-lg p-4 border border-red-500/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">
                  Lines {area.lines[0].index + 1}
                  {area.lines.length > 1 ? `-${area.lines[area.lines.length - 1].index + 1}` : ''}
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                  {area.commonIssue}
                </span>
              </div>
              <p className="text-white/70 text-sm">
                {area.lines.map(l => `"${l.line.text}"`).join(' ')}
              </p>
              <button
                onClick={() => { setShowFocusAreas(false); setTimeout(() => scrollToLine(area.lines[0].index), 100); }}
                className="mt-2 text-xs text-kod-accent hover:text-kod-accent/80 transition-colors"
              >
                Jump to section
              </button>
            </div>
          ))}
        </div>
      ) : (
      <div ref={listContainerRef} className="flex-1 min-h-0 overflow-auto p-4">
        {filteredAnalyses.length === 0 ? (
          <div className="text-center py-12 text-white/50">
            {severityFilter === 'all' ? 'No lyric data available' : `No lines with "${severityFilter}" severity`}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAnalyses.map((analysis) => {
              const isExpanded = expandedLine === analysis.index;
              const badge = getSeverityBadge(analysis.severity);
              const isCurrentLine = fullSongMode && currentLineIndex === analysis.index;

              return (
                <div
                  key={analysis.index}
                  ref={(el) => { lineRefs.current[analysis.index] = el; }}
                  className={`rounded-lg border-l-4 transition-all ${getSeverityColor(analysis.severity)} ${
                    currentPlayingLine === analysis.index || isCurrentLine ? 'ring-2 ring-kod-accent' : ''
                  }`}
                >
                  <button
                    onClick={() => setExpandedLine(isExpanded ? null : analysis.index)}
                    className="w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="text-xs text-white/50 w-12 flex-shrink-0 font-mono">
                      {formatTime(analysis.line.time)}
                    </span>

                    <span className="flex-1 text-white min-w-0 truncate">
                      {analysis.line.text}
                    </span>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {analysis.primaryIssue === 'flat' && (
                        <span className="flex items-center gap-1 text-xs text-blue-400">
                          <TrendingDown size={14} /> Flat
                        </span>
                      )}
                      {analysis.primaryIssue === 'sharp' && (
                        <span className="flex items-center gap-1 text-xs text-orange-400">
                          <TrendingUp size={14} /> Sharp
                        </span>
                      )}
                      {analysis.primaryIssue === 'unstable' && (
                        <span className="flex items-center gap-1 text-xs text-purple-400">
                          <Music size={14} /> Unstable
                        </span>
                      )}

                      <span className={`text-xs px-2 py-0.5 rounded ${badge.class}`}>
                        {badge.text}
                      </span>

                      {isExpanded ? <ChevronDown size={18} className="text-white/50" /> : <ChevronRight size={18} className="text-white/50" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-white/10">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 mb-4">
                        <div className="bg-kod-purple-800/50 rounded p-2 text-center">
                          <div className="text-lg font-semibold text-white">{analysis.avgDeviation.toFixed(3)}</div>
                          <div className="text-xs text-white/60">Avg Deviation</div>
                        </div>
                        <div className="bg-kod-purple-800/50 rounded p-2 text-center">
                          <div className="text-lg font-semibold text-white">{analysis.stdDeviation.toFixed(3)}</div>
                          <div className="text-xs text-white/60">Std Deviation</div>
                        </div>
                        <div className="bg-kod-purple-800/50 rounded p-2 text-center">
                          <div className="text-lg font-semibold text-blue-400">{analysis.flatPercent.toFixed(0)}%</div>
                          <div className="text-xs text-white/60">Time Flat</div>
                        </div>
                        <div className="bg-kod-purple-800/50 rounded p-2 text-center">
                          <div className="text-lg font-semibold text-orange-400">{analysis.sharpPercent.toFixed(0)}%</div>
                          <div className="text-xs text-white/60">Time Sharp</div>
                        </div>
                      </div>

                      {analysis.referencePitch.length > 0 && analysis.recordedPitch.length > 0 && (
                        <div className="bg-kod-purple-900/50 rounded-lg p-3 mb-4" style={{ height: '200px' }}>
                          <Line data={getLineChartData(analysis)} options={chartOptions} />
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-3">
                        {vocalRecordingUrl && (
                          <button
                            onClick={(e) => { e.stopPropagation(); playSection(analysis, 'section'); }}
                            disabled={currentPlayingLine === analysis.index && isPlaying && playbackMode === 'section'}
                            className="flex items-center gap-2 px-4 py-2 bg-kod-accent hover:bg-kod-accent/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
                          >
                            {currentPlayingLine === analysis.index && isPlaying && playbackMode === 'section' ? (
                              <>
                                <Volume2 size={16} className="animate-pulse" /> Playing...
                              </>
                            ) : (
                              <>
                                <Play size={16} /> Play Recording
                              </>
                            )}
                          </button>
                        )}

                        {leadVocalUrl && (
                          <button
                            onClick={(e) => { e.stopPropagation(); playSection(analysis, 'original'); }}
                            disabled={currentPlayingLine === analysis.index && isPlaying && playbackMode === 'original'}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-500/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
                          >
                            {currentPlayingLine === analysis.index && isPlaying && playbackMode === 'original' ? (
                              <>
                                <Volume2 size={16} className="animate-pulse" /> Playing...
                              </>
                            ) : (
                              <>
                                <Disc size={16} /> Play Original
                              </>
                            )}
                          </button>
                        )}

                        {currentPlayingLine === analysis.index && isPlaying && (
                          <button
                            onClick={(e) => { e.stopPropagation(); stopPlayback(); }}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm font-medium transition-colors"
                          >
                            <Pause size={16} /> Stop
                          </button>
                        )}

                        <span className="text-xs text-white/50">
                          {formatTime(analysis.line.time)} - {formatTime(analysis.line.endTime)}
                          {' '}({analysis.dataPointCount} data points)
                        </span>
                      </div>

                      {!vocalRecordingUrl && !leadVocalUrl && (
                        <div className="text-sm text-white/50 italic">
                          Audio playback not available for this recording
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
};
